const INTENSITY_LABEL_CLASS = {
  low: 'bg-green-500/20 text-green-400',
  mid: 'bg-yellow-500/20 text-yellow-400',
  high: 'bg-orange-500/20 text-orange-400',
  max: 'bg-red-500/20 text-red-400',
};

const getIntensityStyle = (value) => {
  if (value <= 2) {
    return { label: 'Leve', className: INTENSITY_LABEL_CLASS.low };
  }
  if (value <= 3) {
    return { label: 'Médio', className: INTENSITY_LABEL_CLASS.mid };
  }
  if (value <= 4) {
    return { label: 'Forte', className: INTENSITY_LABEL_CLASS.high };
  }
  return { label: 'Intenso', className: INTENSITY_LABEL_CLASS.max };
};

const getTanninStyle = (value) => {
  if (value <= 2) {
    return { label: 'Macio', className: 'bg-red-300/20 text-red-300' };
  }
  if (value <= 3) {
    return { label: 'Equilibrado', className: 'bg-red-400/20 text-red-400' };
  }
  if (value <= 4) {
    return { label: 'Presente', className: 'bg-red-500/20 text-red-400' };
  }
  return { label: 'Austero', className: 'bg-red-700/30 text-red-300' };
};

const RED_LEVEL_COLORS = [
  'from-red-300 to-red-400',
  'from-red-400 to-red-500',
  'from-red-500 to-red-600',
  'from-red-600 to-red-700',
  'from-red-700 to-red-900',
];

const IntensityCard = ({ intensity }) => {
  const style = getIntensityStyle(intensity);

  return (
    <div className="group relative bg-gradient-to-br from-[#2d2838] to-[#1f1b28] rounded-2xl p-5 border border-wine-gold/10 hover:border-wine-gold/30 transition-all duration-500 overflow-hidden">
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl group-hover:bg-amber-500/20 transition-all duration-700" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <span className="text-white text-lg">🔥</span>
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm">Intensidade</h4>
            <p className="text-wine-gold/80 text-xs">Corpo e presença</p>
          </div>
        </div>
        <div className="relative h-3 bg-gray-800/80 rounded-full overflow-hidden mb-3">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 transition-all duration-1000 ease-out"
            style={{ width: `${(intensity / 5) * 100}%` }}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex gap-1.5 justify-center">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  level <= intensity
                    ? 'bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-md shadow-orange-500/40'
                    : 'bg-gray-700/50 text-gray-500'
                }`}
              >
                {level}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <span className={`text-sm font-bold px-4 py-1.5 rounded-full ${style.className}`}>
              {style.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TanninCard = ({ tannin }) => {
  const style = getTanninStyle(tannin);

  return (
    <div className="group relative bg-gradient-to-br from-[#2d2838] to-[#1f1b28] rounded-2xl p-5 border border-wine-gold/10 hover:border-wine-gold/30 transition-all duration-500 overflow-hidden">
      <div className="absolute -top-20 -left-20 w-40 h-40 bg-rose-500/10 rounded-full blur-3xl group-hover:bg-rose-500/20 transition-all duration-700" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-900 flex items-center justify-center shadow-lg shadow-red-500/30">
            <span className="text-white text-lg">🍷</span>
          </div>
          <div>
            <h4 className="text-white font-semibold text-sm">Tanino</h4>
            <p className="text-wine-gold/80 text-xs">Secura em boca</p>
          </div>
        </div>
        <div className="relative h-4 bg-gray-800/80 rounded-full overflow-hidden mb-3">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${(tannin / 5) * 100}%`,
              background: 'linear-gradient(90deg, #fca5a5 0%, #f87171 20%, #ef4444 40%, #dc2626 60%, #b91c1c 80%, #7f1d1d 100%)',
            }}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex gap-1.5 justify-center">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  level <= tannin
                    ? `bg-gradient-to-br ${RED_LEVEL_COLORS[level - 1]} text-white shadow-md`
                    : 'bg-gray-700/50 text-gray-500 border border-gray-600/30'
                }`}
              >
                {level}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <span className={`text-sm font-bold px-4 py-1.5 rounded-full ${style.className}`}>
              {style.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SensoryProfile = ({ technical }) => {
  if (!technical.intensity && !technical.tannin) {
    return null;
  }

  return (
    <div className="border-t border-gray-700/50 pt-6 mt-2">
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-wine-gold/30 to-transparent" />
        <span className="text-wine-gold text-xs font-semibold uppercase tracking-widest px-3">Perfil Sensorial</span>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-wine-gold/30 to-transparent" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {technical.intensity && <IntensityCard intensity={technical.intensity} />}
        {technical.tannin && <TanninCard tannin={technical.tannin} />}
      </div>
    </div>
  );
};

export default SensoryProfile;
